import Calendar from '@assets/icons/calendar';
import Clock from '@assets/icons/clock';
import {
  ISubmitBookSessionReq,
  ISubmitBookSessionRes,
  MockExamParent,
  MockExamStudent,
  Schedule,
} from '@models/interfaces/mock_exam.interface';
import mockExamService from '@services/MockExamServices.service';
import { AxiosResponse } from 'axios';
import { Button, Checkbox } from 'geniebook-ui/dist';
import React, { useEffect, useState } from 'react';
import './BookSession.css';
import { ErrorObject } from '@models/interfaces/global.interface';
import moment from 'moment';
import {
  LEVEL_PRIMARY_5,
  MOCK_EXAM_PARENT_DATA_KEY,
  MOCK_EXAM_STEP_KEY,
  MOCK_EXAM_STUDENTS_DATA_KEY,
  PAGE_THANK_YOU,
  SUBJECT_ID_MATH,
} from '@utils/constants/mock-exam.constants';
import {
  MockExamScheduleDate,
  MockExamScheduleDatetime,
} from './styles/BookSession.styles';

const BookSession = ({
  students,
  setStudents,
  regParentData,
  setBookSessionEmailSentTo,
  loading,
  setLoading,
  setError,
  setStep,
  scheduleList,
}: {
  students: MockExamStudent[];
  setStudents: React.Dispatch<React.SetStateAction<MockExamStudent[]>>;
  regParentData: MockExamParent;
  setBookSessionEmailSentTo: React.Dispatch<React.SetStateAction<string>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<ErrorObject>>;
  setStep: React.Dispatch<React.SetStateAction<string>>;
  scheduleList: Schedule[] | null;
}) => {
  const [isFormSubmit, setIsFormSubmit] = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [localTimeZone, setLocalTimeZone] = useState<string>('');
  const p5MathSecondSchedule: Schedule = {
    class_id: 0,
    level_id: LEVEL_PRIMARY_5,
    level_name: 'p5',
    subject_id: SUBJECT_ID_MATH,
    subject_name: 'Mathematics',
    class_date: '2023-09-02',
    class_date_display: 'Mon, 4 Sep',
    start_time: '16:00:00',
    class_time_start: '04:00 PM',
    end_time: '17:40:00',
    class_time_end: '05:40 PM',
  };

  useEffect(() => {
    const momentObj = moment(new Date());
    setLocalTimeZone(momentObj.format('Z').replace(/[0:]/g, ''));
  }, []);

  useEffect(() => {
    let countBookedSchedule = 0;
    students.forEach((student: MockExamStudent) => {
      countBookedSchedule += student.classes.length;
    });

    setIsButtonDisabled(countBookedSchedule === 0 || loading);
  }, [students, loading]);

  const cbScheduleOnChange = (studentIndex: number, class_id: number) => {
    const newStudents: MockExamStudent[] = JSON.parse(JSON.stringify(students));
    if (!isClassEnrolled(class_id, newStudents[studentIndex])) {
      let index = newStudents[studentIndex].classes.indexOf(class_id);
      if (index === -1) {
        index = newStudents[studentIndex].classes.indexOf(
          parseInt(class_id + ''),
        );
        if (index === -1) newStudents[studentIndex].classes.push(class_id);
        else newStudents[studentIndex].classes.splice(index, 1);
      } else newStudents[studentIndex].classes.splice(index, 1);
      setStudents(newStudents);
    }
  };

  const submitBookSession = async () => {
    setIsFormSubmit(true);
    setLoading(true);
    try {
      const submitData: ISubmitBookSessionReq = {
        parent_detail_input: regParentData,
        students: students,
      };
      const res: AxiosResponse =
        await mockExamService.submitBookSession(submitData);
      const body: ISubmitBookSessionRes = res.data;
      if (body.error) {
        setError({
          error: body.error,
          error_msg: body.error_msg,
          error_code: body.error_code,
        });
      } else {
        setBookSessionEmailSentTo(body.data.email);
        // set page index state here
        setStep(PAGE_THANK_YOU);

        localStorage.removeItem(MOCK_EXAM_STEP_KEY);
        localStorage.removeItem(MOCK_EXAM_PARENT_DATA_KEY);
        localStorage.removeItem(MOCK_EXAM_STUDENTS_DATA_KEY);
        // end of set page index state here
      }
    } catch (error) {
      setError({
        error: true,
        error_msg: 'Unknown Error',
        error_code: '',
      });
    } finally {
      setLoading(false);
      setIsFormSubmit(false);
    }
  };

  const convertTZ = (dateString: string, format: string) => {
    const dateStringMoment = moment(dateString);

    // for detecting timezone offset https://stackoverflow.com/a/8090836
    const timezone = parseInt(
      (-(new Date().getTimezoneOffset() / 60)).toString(),
    );
    dateStringMoment.hour(dateStringMoment.hour() - 8 + timezone);

    return dateStringMoment.format(format);
  };

  const isClassEnrolled = (class_id: number, student: MockExamStudent) => {
    if (student.savedClasses === undefined || student.savedClasses.length === 0)
      return false;
    else
      return (
        student.savedClasses.indexOf(class_id) !== -1 ||
        student.savedClasses.indexOf(parseInt(class_id + '')) !== -1
      );
  };

  const isNewClassEnrolled = () => {
    let res: boolean = false;
    students.forEach((student: MockExamStudent) => {
      student.classes.forEach((class_id: number) => {
        if (student.savedClasses !== undefined) {
          if (
            student.savedClasses.indexOf(class_id) === -1 &&
            student.savedClasses.indexOf(parseInt(class_id + '')) === -1
          )
            res = true;
        }
      });
    });

    return res;
  };

  return (
    <>
      <p className="text-neutral-pencil mb-4">
        All timings are in your local time{' '}
        {localTimeZone !== '' ? '(GMT ' + localTimeZone + ')' : ''}.
      </p>
      <div className="max-h-[348px] overflow-auto -ml-2 pl-2 max-sm:max-h-[250px]">
        {scheduleList !== null &&
          students
            .filter(el => el.checked === undefined || el.checked)
            .map((student: MockExamStudent, studentIndex: number) => {
              return (
                <div
                  className="student-schedule-container mb-6"
                  key={studentIndex}
                >
                  <p className="mock-exam-student-name text-neutral-pencil font-medium overflow-hidden break-all">
                    {(
                      student.first_name +
                      ' ' +
                      (student.last_name === null ? '' : student.last_name)
                    ).trim()}
                    {(
                      student.first_name +
                      ' ' +
                      (student.last_name === null ? '' : student.last_name)
                    ).trim().length <= 15 ? (
                      <span className="mock-exam-student-level text-neutral-dust text-[14px] ml-2">
                        {student.level_name}
                      </span>
                    ) : (
                      ''
                    )}
                  </p>
                  {(
                    student.first_name +
                    ' ' +
                    (student.last_name === null ? '' : student.last_name)
                  ).trim().length > 15 ? (
                    <span className="mock-exam-student-level text-neutral-dust text-[14px]">
                      {student.level_name}
                    </span>
                  ) : (
                    ''
                  )}
                  {scheduleList
                    .filter(el => el.level_id + '' === student.level_id + '')
                    .map((schedule: Schedule) => {
                      return (
                        <div
                          className={
                            'mock-exam-schedule-container flex justify-start items-start py-4 border-solid border-0 border-b-[1px] border-neutral-notebook' +
                            (isClassEnrolled(schedule.class_id, student)
                              ? ' enrolled'
                              : '')
                          }
                          key={schedule.class_id}
                        >
                          <div className="mock-exam-schedule-checkbox-container w-[21]">
                            <Checkbox
                              className="cursor-pointer"
                              variant={'light'}
                              isChecked={
                                student.classes.indexOf(schedule.class_id) !=
                                  -1 ||
                                student.classes.indexOf(
                                  parseInt(schedule.class_id + ''),
                                ) != -1
                              }
                              isValidation={false}
                              onChange={() => {
                                cbScheduleOnChange(
                                  studentIndex,
                                  schedule.class_id,
                                );
                              }}
                              label={''}
                              disabled={
                                isClassEnrolled(schedule.class_id, student) ||
                                isFormSubmit
                              }
                            />
                          </div>
                          <div
                            className="mock-exam-schedule-desc pl-2 w-full cursor-pointer"
                            onClick={() => {
                              cbScheduleOnChange(
                                studentIndex,
                                schedule.class_id,
                              );
                            }}
                          >
                            <div className="flex justify-start items-center">
                              <p
                                className={`
                                  mock-exam-schedule-title font-medium leading-[21px] text-neutral-pencil mb-1
                                  ${
                                    isClassEnrolled(schedule.class_id, student)
                                      ? 'text-neutral-dust'
                                      : ''
                                  }
                                `}
                              >
                                {schedule.level_name} {schedule.subject_name}
                              </p>
                              {isClassEnrolled(schedule.class_id, student) && (
                                <div className="mock-exam-schedule-enrolled bg-neutral-dust text-neutral-whiteboard text-[10px] font-bold px-2 py-1 rounded-lg ml-2">
                                  Enrolled
                                </div>
                              )}
                            </div>

                            {schedule.level_id == LEVEL_PRIMARY_5 &&
                            schedule.subject_id == SUBJECT_ID_MATH ? (
                              <div className="mock-exam-schedule-paper-container">
                                <MockExamScheduleDatetime className="mock-exam-schedule-datetime !block mb-1 max-sm:!mb-4">
                                  <div>
                                    <label className="font-medium mr-2 float-left">
                                      Paper 1
                                    </label>
                                  </div>
                                  <MockExamScheduleDate className="mock-exam-schedule-date float-left pr-2">
                                    <Calendar className="h-3" />
                                    <span>{schedule.class_date_display}</span>
                                  </MockExamScheduleDate>
                                  <MockExamScheduleDate className="mock-exam-schedule-time float-left">
                                    <Clock className="h-3" />
                                    <span>
                                      {convertTZ(
                                        schedule.class_date +
                                          ' ' +
                                          schedule.start_time,
                                        'LT',
                                      ) +
                                        ' - ' +
                                        convertTZ(
                                          schedule.class_date +
                                            ' ' +
                                            schedule.end_time,
                                          'LT',
                                        )}
                                    </span>
                                  </MockExamScheduleDate>
                                  <div className="clear-both"></div>
                                </MockExamScheduleDatetime>
                                <MockExamScheduleDatetime className="mock-exam-schedule-datetime !block mb-1">
                                  <div>
                                    <label className="font-medium mr-2 float-left">
                                      Paper 2
                                    </label>
                                  </div>
                                  <MockExamScheduleDate className="mock-exam-schedule-date float-left pr-2">
                                    <Calendar />
                                    <span>
                                      {p5MathSecondSchedule.class_date_display}
                                    </span>
                                  </MockExamScheduleDate>
                                  <MockExamScheduleDate className="mock-exam-schedule-time float-left">
                                    <Clock />
                                    <span>
                                      {convertTZ(
                                        p5MathSecondSchedule.class_date +
                                          ' ' +
                                          p5MathSecondSchedule.start_time,
                                        'LT',
                                      ) +
                                        ' - ' +
                                        convertTZ(
                                          p5MathSecondSchedule.class_date +
                                            ' ' +
                                            p5MathSecondSchedule.end_time,
                                          'LT',
                                        )}
                                    </span>
                                  </MockExamScheduleDate>
                                  <div className="clear-both"></div>
                                </MockExamScheduleDatetime>
                              </div>
                            ) : (
                              <MockExamScheduleDatetime className="mock-exam-schedule-datetime">
                                <MockExamScheduleDate className="mock-exam-schedule-date pr-2">
                                  <Calendar />
                                  <span>{schedule.class_date_display}</span>
                                </MockExamScheduleDate>
                                <MockExamScheduleDate className="mock-exam-schedule-time">
                                  <Clock />
                                  <span>
                                    {convertTZ(
                                      schedule.class_date +
                                        ' ' +
                                        schedule.start_time,
                                      'LT',
                                    ) +
                                      ' - ' +
                                      convertTZ(
                                        schedule.class_date +
                                          ' ' +
                                          schedule.end_time,
                                        'LT',
                                      )}
                                  </span>
                                </MockExamScheduleDate>
                              </MockExamScheduleDatetime>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              );
            })}
      </div>
      <Button
        variant="primary"
        onClick={submitBookSession}
        className="max-w-full"
        disabled={!isNewClassEnrolled() || isButtonDisabled}
      >
        Book sessions
      </Button>
    </>
  );
};

export default BookSession;
